import { ErrorHandler, inject, Injectable, isDevMode } from '@angular/core';

import { NotificationsService } from '../notifications/notifications.service';
import { AppError } from './app-error';

/**
 * Catch-all for uncaught errors. Logs to console in dev, surfaces a generic
 * toast to the user, and stays out of the way of feature code that handles
 * its own errors locally.
 *
 * Sentry will be wired in later via `Sentry.createErrorHandler()`; until then
 * this handler is the single place to add reporting.
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private readonly notifications = inject(NotificationsService);

  handleError(error: unknown): void {
    const normalised = error instanceof AppError ? error : this.normalise(error);

    if (isDevMode()) {
      console.error('[AppErrorHandler]', normalised, error);
    }

    if (normalised.kind !== 'unauthorized' && normalised.kind !== 'forbidden') {
      this.notifications.error(this.userMessageFor(normalised));
    }
  }

  private normalise(error: unknown): AppError {
    if (error instanceof Error) {
      return new AppError('unknown', error.message || 'Something went wrong', { cause: error });
    }
    return new AppError('unknown', 'Something went wrong', { cause: error });
  }

  private userMessageFor(error: AppError): string {
    switch (error.kind) {
      case 'network':
        return 'We could not reach Growzz. Check your connection and try again.';
      case 'forbidden':
        return "You don't have permission to do that.";
      case 'not_found':
        return 'That item could not be found.';
      case 'conflict':
        return 'This item changed while you were editing it. Please refresh and try again.';
      case 'validation':
        return error.message || 'Some of the details are invalid.';
      case 'server':
        return 'Growzz hit an error. Please try again in a moment.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }
}
